import { Skeleton } from '@avanta/shared/components/ui/Skeleton';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { cn } from '@avanta/shared/utils/cn';
import { useFormContext, useWatch } from 'react-hook-form';
import { SessionSummarySkeleton } from 'pages/AppointmentsReserve/components/ui/SessionSummary/SessionSummarySkeleton';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { hasFixedPsychologist } from 'pages/AppointmentsReserve/utils';
import { AppointmentsCalendarSection } from './AppointmentsCalendarSection';

const WEEKDAY_HEADERS = 7;
const CALENDAR_ROWS = 5;
const DAYS_PER_WEEK = 7;
const TIME_SLOTS_COUNT = 9;

const BannerSkeleton = (): React.ReactElement => (
  <div className="flex w-full items-center justify-between rounded-xl bg-plum-100 py-3 pr-6 pl-4">
    <div className="flex flex-col gap-1">
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-4 w-80" />
    </div>
    <Skeleton className="h-4 w-28" />
  </div>
);

const CalendarGridSkeleton = (): React.ReactElement => (
  <div className="flex w-full max-w-75 shrink-0 flex-col gap-4">
    <div className="relative flex h-8 items-center justify-center">
      <Skeleton className="absolute left-0 size-8 rounded-md" />
      <Skeleton className="h-5 w-24" />
      <Skeleton className="absolute right-0 size-8 rounded-md" />
    </div>
    <div className="flex flex-col">
      <div className="flex">
        {Array.from({ length: WEEKDAY_HEADERS }, (_weekday, weekdayIndex) => (
          <div key={`weekday-${weekdayIndex}`} className="flex flex-1 justify-center">
            <Skeleton className="h-4 w-6" />
          </div>
        ))}
      </div>
      {Array.from({ length: CALENDAR_ROWS }, (_week, weekIndex) => (
        <div key={`week-${weekIndex}`} className="mt-2 flex w-full">
          {Array.from({ length: DAYS_PER_WEEK }, (_day, dayIndex) => (
            <div
              key={`day-${weekIndex}-${dayIndex}`}
              className="flex flex-1 items-center justify-center"
            >
              <Skeleton className="size-9 rounded-full" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const TimeSlotsSkeleton = (): React.ReactElement => (
  <div className="flex w-full shrink-0 flex-col gap-2">
    <Skeleton className="h-4 w-24" />
    <div className="grid w-full max-w-51 grid-cols-3 gap-2">
      {Array.from({ length: TIME_SLOTS_COUNT }, (_slot, slotIndex) => (
        <Skeleton key={`slot-${slotIndex}`} className="h-8 w-full rounded" />
      ))}
    </div>
  </div>
);

export const SessionStepSkeleton = (): React.ReactElement => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [backofficeCenterId, dateTime, flow] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.backofficeCenterId,
      AppointmentsReservePayload.dateTime,
      AppointmentsReservePayload.flow,
    ],
  });
  const isEditFlow = flow === FLOW_MODES.edit;
  const showBanner = !dateTime && !hasFixedPsychologist(flow);

  return (
    <div className="flex flex-col gap-8">
      {showBanner ? <BannerSkeleton /> : null}
      <div className="flex w-241 gap-8">
        <SessionSummarySkeleton
          sections={{
            dateTime: Boolean(dateTime),
            patient: true,
            service: true,
            psychologist: false,
          }}
        />
        {backofficeCenterId ? (
          <AppointmentsCalendarSection className="flex h-full justify-between gap-16 rounded-xl border border-teal-300 p-6">
            <CalendarGridSkeleton />
            <TimeSlotsSkeleton />
          </AppointmentsCalendarSection>
        ) : null}
      </div>
      <div className={cn('flex w-full justify-between', isEditFlow && 'justify-end')}>
        {isEditFlow ? null : <Skeleton className="h-10 w-28 rounded-[20px]" />}
        <Skeleton className="h-10 w-37 min-w-37 rounded-[20px]" />
      </div>
    </div>
  );
};
